import type { useNavigate } from '@change-corgi/core-react/router';
import type { TrackingFn } from '@change-corgi/core-react/tracking';

import type { AttachedPost } from './attachedPost';
import { toAttachedPost } from './attachedPost';
import type { SocialPostErrorCode } from './errorCodes';
import { classifySocialPostInput } from './utils';
import type { ClassifyOptions } from './utils';

type FreshSubmitContext = {
	classifyOptions: ClassifyOptions;
	track: TrackingFn;
	navigate: ReturnType<typeof useNavigate>;
	isSubmitting: boolean;
	setAttachedPost: (post: AttachedPost) => void;
	setBlockError: (code: SocialPostErrorCode | undefined) => void;
	createIssueDraftMutate: (text: string) => void;
};

/**
 * A supported link attaches and starts its preview, a blocked link surfaces its error, freeform
 * text starts an issue draft, and empty input falls through to the plain SAP intro. onSubmit's
 * `attachedPost` branch handles the rest.
 */
export function submitFreshInput(trimmed: string, ctx: FreshSubmitContext): void {
	const classification = classifySocialPostInput(trimmed, ctx.classifyOptions);

	// A supported link is the only case that diverges from the SAP hero under the hood
	// (it resolves via generateAiDraftFromSocialPost instead of a plain ai_draft); freeform
	// text — including an empty submit, which also classifies as freeform — takes the exact
	// same path as the SAP hero, so it's tracked under the same event.
	void ctx.track(classification.kind === 'freeform' ? 'homepage_SAP_click' : 'homepage_social_post_click', {
		cta: 'primary',
		prompt: trimmed,
	});

	if (ctx.isSubmitting) return;

	if (!trimmed.length) {
		ctx.navigate('/start-a-petition/intro?source_location=homepage_tiktok');
		return;
	}

	switch (classification.kind) {
		case 'supported_link':
			// A link typed by hand: attach it and kick off its preview, same as a pasted link.
			// The user submits again once the chip renders with a resolved preview.
			ctx.setAttachedPost(toAttachedPost(classification));
			break;
		case 'unsupported_platform':
		case 'invalid_link':
			// Gives the click an outcome: the submit was rejected client-side before any
			// generation, so it never reaches the success/error events above.
			ctx.setBlockError(classification.kind);
			void ctx.track('homepage_social_post_blocked', { reason: classification.kind, prompt: trimmed });
			break;
		case 'freeform':
			ctx.createIssueDraftMutate(trimmed);
			break;
		default:
			break;
	}
}
